import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../axiosConfig';
import PublicacionCard from './PublicacionCard';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** OpenAlex type "preprint" — no subir a la base. */
function isPreprintPublication(pub) {
  const t = String(pub?.type ?? pub?.tipo ?? '').trim().toLowerCase();
  return t === 'preprint';
}

function normalizeOpenAlexAuthorId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const withoutUrl = raw.replace(/^https?:\/\/openalex\.org\//i, '');
  return withoutUrl.toUpperCase();
}

function mapDatabasePublicationToMerged(pub) {
  const mergedId = pub?.open_alex_id || pub?.id ? String(pub.open_alex_id || pub.id) : `db-${Math.random()}`;
  return {
    ...pub,
    mergedPublicationId: mergedId,
    title: pub?.titulo || pub?.title || '',
    year: pub?.anio_publicacion ?? pub?.anio ?? pub?.year ?? null,
    publicationDate: pub?.fecha_publicacion || pub?.publicationDate || null,
    type: pub?.tipo || pub?.type || null,
    journal: pub?.journal || null,
    publisher: pub?.publisher || null,
    volume: pub?.volume || null,
    issue: pub?.issue || null,
    doi: pub?.doi || null,
    openAlexId: pub?.open_alex_id || pub?.openAlexId || null,
    source: pub?.source || 'manual',
    url: pub?.external_url || pub?.url || null,
    oaUrl: pub?.oa_url || pub?.oaUrl || null,
    oaStatus: pub?.oa_status || pub?.oaStatus || null,
    isOpenAccess: pub?.is_open_access ?? pub?.isOpenAccess ?? false,
    citationsCount: pub?.citations_count ?? pub?.citationsCount ?? 0,
    referencedWorksCount: pub?.referenced_works_count ?? pub?.referencedWorksCount ?? 0,
    isTop10Percent: pub?.is_top_10_percent ?? pub?.isTop10Percent ?? false,
    fwci: pub?.fwci ?? null,
    language: pub?.language || null,
    abstract: pub?.abstract || null,
    authors: Array.isArray(pub?.authors_json)
      ? pub.authors_json.map((author) => ({
          name: author?.name || author?.display_name || 'Autor desconocido',
          openAlexId: author?.openAlexId || author?.open_alex_id || null,
          orcid: author?.orcid || null,
          position: author?.position || null,
          is_corresponding: author?.is_corresponding || false,
          institutions: author?.institutions || [],
        }))
      : Array.isArray(pub?.autores)
        ? pub.autores.map((name) => ({ name }))
        : [],
  };
}

// Componente para cada ítem sortable
function SortablePublicacionCard({ pub, index, containerId, isPending }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: pub.mergedPublicationId,
    data: {
      containerId,
      pub,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 150ms cubic-bezier(0.2, 0, 0, 1)',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.02, duration: 0.15 }}
        className={`relative ${isDragging ? 'z-50' : ''}`}
      >
        {/* Handle de drag */}
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-l-xl transition-colors"
        >
          <span className="material-icons-round text-slate-400 text-lg">drag_indicator</span>
        </div>
        <div className="pl-8">
          <div className={`rounded-lg border-2 transition-colors ${
            isPending 
              ? 'border-yellow-400 bg-yellow-50/50 dark:bg-yellow-900/10' 
              : 'border-transparent'
          }`}>
            <PublicacionCard
              pub={pub}
              selected={false}
              alreadySaved={containerId === 'database' && !isPending}
              onToggle={() => {}}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Componente para el overlay mientras se arrastra
function DragOverlayCard({ pub, containerId }) {
  return (
    <div className="relative opacity-90 rotate-2 scale-105 shadow-2xl">
      <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-l-xl">
        <span className="material-icons-round text-slate-400 text-lg">drag_indicator</span>
      </div>
      <div className="pl-8">
        <PublicacionCard
          pub={pub}
          selected={false}
          alreadySaved={containerId === 'database'}
          onToggle={() => {}}
        />
      </div>
    </div>
  );
}

// Contenedor de drop
function DroppableContainer({ id, title, count, icon, children, color = 'blue' }) {
  const { setNodeRef, isOver } = useSortable({
    id,
    data: {
      type: 'container',
      id,
    },
  });

  const colorClasses = {
    blue: 'border-[#2D5BA6] bg-blue-50/50 dark:bg-blue-900/10',
    green: 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10',
    amber: 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
    yellow: 'border-yellow-500 bg-yellow-50/50 dark:bg-yellow-900/10',
  };

  const headerColors = {
    blue: 'bg-[#2D5BA6]',
    green: 'bg-emerald-600',
    amber: 'bg-amber-600',
    yellow: 'bg-yellow-500',
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col h-full rounded-xl border-2 transition-all duration-150 ${
        isOver ? colorClasses[color] : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
      }`}
    >
      {/* Header */}
      <div className={`${headerColors[color]} text-white px-4 py-3 rounded-t-lg flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className="material-icons-round">{icon}</span>
          <span className="font-semibold">{title}</span>
        </div>
        <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm font-medium">
          {count}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 overflow-y-auto max-h-[55vh] space-y-2">
        {children}
      </div>
    </div>
  );
}

// Componente para mostrar una variante individual de autor (como en OpenAlex UI)
function AuthorVariantItem({ variant, isSelected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!variant.alreadyInDB) {
          onToggle(variant.openAlexId);
        }
      }}
      disabled={variant.alreadyInDB}
      className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
        variant.alreadyInDB
          ? 'border-emerald-500 bg-emerald-50/80 dark:bg-emerald-900/10 cursor-not-allowed'
          : isSelected
            ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/10'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-white">{variant.name}</span>
            {/* Conteo de obras como en OpenAlex: "Maribel Maya (9)" */}
            <span className="text-sm text-slate-500 dark:text-slate-400">
              ({variant.worksCount || 0})
            </span>
            {variant.alreadyInDB && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full">
                <span className="material-icons-round text-sm">check_circle</span>
                Ya vinculado
              </span>
            )}
          </div>
          {variant.affiliation && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1">
              <span className="material-icons-round text-sm">business</span>
              {variant.affiliation}
            </div>
          )}
        </div>
        <span
          className={`material-icons-round flex-shrink-0 ${
            variant.alreadyInDB 
              ? 'text-emerald-600 dark:text-emerald-400' 
              : isSelected 
                ? 'text-[#2D5BA6]' 
                : 'text-slate-400'
          }`}
        >
          {variant.alreadyInDB ? 'done_all' : isSelected ? 'check_circle' : 'radio_button_unchecked'}
        </span>
      </div>
    </button>
  );
}

function StepPublicacionesExtra({
  universidad,
  investigador,
  savedPublicaciones = [],
  saving,
  saveResult,
  initialAuthorSearchText = '',
  onBack,
  onSave,
}) {
  // Sub-steps internos:
  // 1) Buscar variantes de autor (filtrado por institución)
  // 2) Buscar publicaciones usando open_alex_ids guardados
  const [subStep, setSubStep] = useState(1);

  const [authorSearchText, setAuthorSearchText] = useState(initialAuthorSearchText || '');
  const [authorVariants, setAuthorVariants] = useState([]);
  const [authorLoading, setAuthorLoading] = useState(false);
  const [authorLoadingError, setAuthorLoadingError] = useState(null);
  const authorSearchRequestIdRef = useRef(0);
  const authorSearchTimeoutRef = useRef(null);
  const [titleSearchText, setTitleSearchText] = useState('');
  const [showOpenAlexHelp, setShowOpenAlexHelp] = useState(false);

  const [selectedAuthorIds, setSelectedAuthorIds] = useState(new Set());
  const [currentMainOpenAlexId, setCurrentMainOpenAlexId] = useState(() =>
    normalizeOpenAlexAuthorId(investigador?.open_alex_id)
  );
  const [currentAlternateOpenAlexIds, setCurrentAlternateOpenAlexIds] = useState(() =>
    Array.isArray(investigador?.open_alex_ids)
      ? investigador.open_alex_ids
          .map((id) => normalizeOpenAlexAuthorId(id))
          .filter(Boolean)
      : []
  );
  const [extraPublicaciones, setExtraPublicaciones] = useState([]);
  const [pubLoading, setPubLoading] = useState(false);
  const [pubLoadingError, setPubLoadingError] = useState(null);
  const [pubSearchMode, setPubSearchMode] = useState('author');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [libraryFilters, setLibraryFilters] = useState({
    title: '',
    year_from: '',
    year_to: '',
    keyword: '',
  });
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  const [libraryResults, setLibraryResults] = useState([]);
  const [librarySelectedPubs, setLibrarySelectedPubs] = useState([]);
  const [libraryActiveId, setLibraryActiveId] = useState(null);
  const [libraryActiveContainer, setLibraryActiveContainer] = useState(null);

  // Estado para drag and drop
  const [activeId, setActiveId] = useState(null);
  const [activeContainer, setActiveContainer] = useState(null);

  // Publicaciones en cada contenedor
  const [databasePubs, setDatabasePubs] = useState([]);
  const [foundPubs, setFoundPubs] = useState([]);
  
  // IDs de publicaciones pendientes de guardar (movidas a database)
  const [pendingIds, setPendingIds] = useState(new Set());

  // Sensores para dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const investigatorOpenAlexIds = useMemo(() => {
    const ids = new Set();
    const main = currentMainOpenAlexId;
    if (main) ids.add(String(main));
    const alternates = currentAlternateOpenAlexIds || [];
    if (Array.isArray(alternates)) {
      alternates.forEach((x) => {
        if (x) ids.add(String(x));
      });
    }
    return ids;
  }, [currentMainOpenAlexId, currentAlternateOpenAlexIds]);

  const savedOpenAlexIds = useMemo(() => {
    const ids = new Set();
    savedPublicaciones.forEach((p) => {
      if (p?.open_alex_id) ids.add(String(p.open_alex_id));
    });
    return ids;
  }, [savedPublicaciones]);

  // Inicializar las listas cuando cambien las publicaciones
  useEffect(() => {
    setDatabasePubs(savedPublicaciones.map(p => ({
      ...p,
      mergedPublicationId: p.open_alex_id || p.id || `db-${Math.random()}`,
      title: p.titulo,
      authors: p.autores?.map(a => ({ name: a })) || [],
      year: p.anio_publicacion,
    })));
  }, [savedPublicaciones]);

  useEffect(() => {
    // Filtrar las que no están en la base de datos
    const newPubs = extraPublicaciones.filter(
      pub => !savedOpenAlexIds.has(String(pub.openAlexId))
    );
    setFoundPubs(newPubs);
  }, [extraPublicaciones, savedOpenAlexIds]);

  const toggleAuthorId = (openAlexId) => {
    if (investigatorOpenAlexIds.has(String(openAlexId))) return;
    setSelectedAuthorIds((prev) => {
      const next = new Set(prev);
      if (!openAlexId) return next;
      if (next.has(openAlexId)) next.delete(openAlexId);
      else next.add(openAlexId);
      return next;
    });
  };

  const handleSave = () => {
    // Guardar las publicaciones pendientes (las que están en databasePubs pero no en savedPublicaciones original)
    const pubsToSave = databasePubs.filter(pub => {
      if (isPreprintPublication(pub)) return false;
      const isOriginallySaved = savedPublicaciones.some(sp => 
        String(sp.open_alex_id) === String(pub.openAlexId || pub.open_alex_id)
      );
      return !isOriginallySaved;
    });
    
    if (pubsToSave.length === 0) {
      return; // No hay nada nuevo para guardar
    }
    
    onSave(pubsToSave);
  };

  // Handler para iniciar drag
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Determinar de qué contenedor viene
    const dbItem = databasePubs.find(p => p.mergedPublicationId === active.id);
    if (dbItem) {
      setActiveContainer('database');
    } else {
      setActiveContainer('found');
    }
  };

  // Handler para finalizar drag
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveContainer(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Determinar contenedores
    const activeInDb = databasePubs.find(p => p.mergedPublicationId === activeId);
    
    const overIsContainer = overId === 'database' || overId === 'found';
    const overInDb = databasePubs.find(p => p.mergedPublicationId === overId);
    const overInFound = foundPubs.find(p => p.mergedPublicationId === overId);

    const sourceContainer = activeInDb ? 'database' : 'found';
    let targetContainer = sourceContainer;
    if (overIsContainer) {
      targetContainer = overId;
    } else if (overInDb) {
      targetContainer = 'database';
    } else if (overInFound) {
      targetContainer = 'found';
    }

    // Si es el mismo contenedor, solo reordenar
    if (sourceContainer === targetContainer) {
      if (sourceContainer === 'database') {
        const oldIndex = databasePubs.findIndex(p => p.mergedPublicationId === activeId);
        const newIndex = databasePubs.findIndex(p => p.mergedPublicationId === overId);
        if (oldIndex !== newIndex && newIndex !== -1) {
          setDatabasePubs(arrayMove(databasePubs, oldIndex, newIndex));
        }
      } else {
        const oldIndex = foundPubs.findIndex(p => p.mergedPublicationId === activeId);
        const newIndex = foundPubs.findIndex(p => p.mergedPublicationId === overId);
        if (oldIndex !== newIndex && newIndex !== -1) {
          setFoundPubs(arrayMove(foundPubs, oldIndex, newIndex));
        }
      }
      return;
    }

    // Si es diferente contenedor, mover el item
    if (sourceContainer === 'found' && targetContainer === 'database') {
      // Mover de found a database = pendiente de guardar
      const item = foundPubs.find(p => p.mergedPublicationId === activeId);
      if (item) {
        setFoundPubs(prev => prev.filter(p => p.mergedPublicationId !== activeId));
        setDatabasePubs(prev => [...prev, item]);
        setPendingIds(prev => new Set([...prev, activeId]));
      }
    } else if (sourceContainer === 'database' && targetContainer === 'found') {
      // Mover de database a found = quitar de pendientes
      const item = databasePubs.find(p => p.mergedPublicationId === activeId);
      if (item) {
        setDatabasePubs(prev => prev.filter(p => p.mergedPublicationId !== activeId));
        setFoundPubs(prev => [...prev, item]);
        setPendingIds(prev => {
          const next = new Set(prev);
          next.delete(activeId);
          return next;
        });
      }
    }
  };

  const fetchAuthorCandidates = async (authorName) => {
    const q = (authorName || '').trim();
    if (!q || q.length < 2) {
      setAuthorVariants([]);
      setAuthorLoading(false);
      setAuthorLoadingError(null);
      return;
    }
    const reqId = ++authorSearchRequestIdRef.current;
    setAuthorLoading(true);
    setAuthorLoadingError(null);
    setAuthorVariants([]);

    try {
      const res = await api.get(`/api/investigadores/${investigador.id}/openalex-authors-by-name-and-institution`, {
        params: {
          author_name: q,
          year_start: 2020,
          year_end: 2026
        },
      });

      if (reqId !== authorSearchRequestIdRef.current) return;
      setAuthorVariants(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (reqId !== authorSearchRequestIdRef.current) return;
      setAuthorLoadingError(err.response?.data?.detail || 'Error buscando autores en OpenAlex');
      setAuthorVariants([]);
    } finally {
      if (reqId !== authorSearchRequestIdRef.current) return;
      setAuthorLoading(false);
    }
  };

  // Guardar los IDs seleccionados y buscar publicaciones
  const saveSelectedAndFetchPublications = async () => {
    const ids = Array.from(selectedAuthorIds).filter(Boolean).filter((id) => !investigatorOpenAlexIds.has(String(id)));
    
    // Guardar IDs seleccionados (nuevos)
    if (ids.length > 0) {
      await api.post(`/api/investigadores/${investigador.id}/openalex-ids`, { open_alex_ids: ids });
      setCurrentAlternateOpenAlexIds((prev) =>
        Array.from(new Set([...prev, ...ids].map((id) => normalizeOpenAlexAuthorId(id)).filter(Boolean)))
      );
    }
    
    // Buscar publicaciones con TODOS los IDs del investigador (los que ya estaban + los nuevos)
    await fetchPublicationsUsingAllIds();
  };

  // Buscar publicaciones usando todos los IDs de OpenAlex del investigador
  const fetchPublicationsUsingAllIds = async () => {
    setPubSearchMode('author');
    setPubLoading(true);
    setPubLoadingError(null);
    setExtraPublicaciones([]);

    try {
      // El endpoint ya busca con todos los IDs (open_alex_id + open_alex_ids[])
      const res = await api.get(`/api/investigadores/${investigador.id}/openalex-publications`, {
        params: { year_start: 2020, year_end: 2026 },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setExtraPublicaciones(list.filter((p) => !isPreprintPublication(p)));
    } catch (err) {
      setPubLoadingError(err.response?.data?.detail || 'Error buscando publicaciones en OpenAlex');
      setExtraPublicaciones([]);
    } finally {
      setPubLoading(false);
    }
  };

  // Ir directamente a buscar publicaciones con los IDs actuales (sin guardar nuevos)
  const handleSkipAuthorSelection = async () => {
    setPubLoading(true);
    setPubLoadingError(null);
    setSubStep(2);
    await fetchPublicationsUsingAllIds();
  };

  const searchPublicationsByTitle = async () => {
    const q = (titleSearchText || '').trim();
    if (q.length < 3) {
      setPubLoadingError('Ingresa al menos 3 caracteres del título para buscar.');
      return;
    }

    setPubSearchMode('title');
    setSubStep(2);
    setPubLoading(true);
    setPubLoadingError(null);
    setExtraPublicaciones([]);

    try {
      const res = await api.get(`/api/investigadores/${investigador.id}/openalex-publications-by-title`, {
        params: { title: q, year_start: 2020, year_end: 2026 },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setExtraPublicaciones(list.filter((p) => !isPreprintPublication(p)));
    } catch (err) {
      setPubLoadingError(err.response?.data?.detail || 'Error buscando publicaciones por título en OpenAlex');
      setExtraPublicaciones([]);
    } finally {
      setPubLoading(false);
    }
  };

  const fetchLibraryPublications = async () => {
    setLibraryLoading(true);
    setLibraryError(null);
    try {
      const params = { limit: 2000 };
      if (libraryFilters.title.trim()) params.title = libraryFilters.title.trim();
      if (libraryFilters.year_from) params.year_from = libraryFilters.year_from;
      if (libraryFilters.year_to) params.year_to = libraryFilters.year_to;
      if (libraryFilters.keyword.trim()) params.keyword = libraryFilters.keyword.trim();

      const res = await api.get('/api/publicaciones/', { params });
      const list = Array.isArray(res.data) ? res.data : [];
      const currentIds = new Set(databasePubs.map((pub) => String(pub.mergedPublicationId)));
      const selectedIds = new Set(librarySelectedPubs.map((pub) => String(pub.mergedPublicationId)));

      const mapped = list
        .map(mapDatabasePublicationToMerged)
        .filter((pub) => !currentIds.has(String(pub.mergedPublicationId)) && !selectedIds.has(String(pub.mergedPublicationId)));

      setLibraryResults(mapped);
    } catch (err) {
      setLibraryError(err.response?.data?.detail || 'No se pudo buscar en el módulo de publicaciones.');
      setLibraryResults([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleLibraryDragStart = (event) => {
    const { active } = event;
    setLibraryActiveId(active.id);
    const selectedItem = librarySelectedPubs.find((p) => p.mergedPublicationId === active.id);
    setLibraryActiveContainer(selectedItem ? 'library-selected' : 'library-results');
  };

  const handleLibraryDragEnd = (event) => {
    const { active, over } = event;
    setLibraryActiveId(null);
    setLibraryActiveContainer(null);

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const activeInSelected = librarySelectedPubs.find((p) => p.mergedPublicationId === activeId);
    const overIsContainer = overId === 'library-results' || overId === 'library-selected';
    const overInSelected = librarySelectedPubs.find((p) => p.mergedPublicationId === overId);
    const overInResults = libraryResults.find((p) => p.mergedPublicationId === overId);

    const sourceContainer = activeInSelected ? 'library-selected' : 'library-results';
    let targetContainer = sourceContainer;
    if (overIsContainer) {
      targetContainer = overId;
    } else if (overInSelected) {
      targetContainer = 'library-selected';
    } else if (overInResults) {
      targetContainer = 'library-results';
    }

    if (sourceContainer === targetContainer) {
      if (sourceContainer === 'library-selected') {
        const oldIndex = librarySelectedPubs.findIndex((p) => p.mergedPublicationId === activeId);
        const newIndex = librarySelectedPubs.findIndex((p) => p.mergedPublicationId === overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          setLibrarySelectedPubs(arrayMove(librarySelectedPubs, oldIndex, newIndex));
        }
      } else {
        const oldIndex = libraryResults.findIndex((p) => p.mergedPublicationId === activeId);
        const newIndex = libraryResults.findIndex((p) => p.mergedPublicationId === overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          setLibraryResults(arrayMove(libraryResults, oldIndex, newIndex));
        }
      }
      return;
    }

    if (sourceContainer === 'library-results' && targetContainer === 'library-selected') {
      const item = libraryResults.find((p) => p.mergedPublicationId === activeId);
      if (!item) return;
      setLibraryResults((prev) => prev.filter((p) => p.mergedPublicationId !== activeId));
      setLibrarySelectedPubs((prev) => [...prev, item]);
      return;
    }

    if (sourceContainer === 'library-selected' && targetContainer === 'library-results') {
      const item = librarySelectedPubs.find((p) => p.mergedPublicationId === activeId);
      if (!item) return;
      setLibrarySelectedPubs((prev) => prev.filter((p) => p.mergedPublicationId !== activeId));
      setLibraryResults((prev) => [...prev, item]);
    }
  };

  const applyLibrarySelection = () => {
    if (librarySelectedPubs.length === 0) {
      setShowSearchModal(false);
      return;
    }

    setDatabasePubs((prev) => {
      const existingIds = new Set(prev.map((pub) => String(pub.mergedPublicationId)));
      const next = [...prev];
      librarySelectedPubs.forEach((pub) => {
        if (!existingIds.has(String(pub.mergedPublicationId))) {
          next.push(pub);
          existingIds.add(String(pub.mergedPublicationId));
        }
      });
      return next;
    });

    setPendingIds((prev) => {
      const next = new Set(prev);
      librarySelectedPubs.forEach((pub) => next.add(pub.mergedPublicationId));
      return next;
    });

    setLibrarySelectedPubs([]);
    setLibraryResults([]);
    setShowSearchModal(false);
  };

  useEffect(() => {
    // reset al cambiar de investigador
    setSubStep(1);
    setAuthorSearchText(initialAuthorSearchText || '');
    setTitleSearchText('');
    setSelectedAuthorIds(new Set());
    setCurrentMainOpenAlexId(normalizeOpenAlexAuthorId(investigador?.open_alex_id));
    setCurrentAlternateOpenAlexIds(
      Array.isArray(investigador?.open_alex_ids)
        ? investigador.open_alex_ids
            .map((id) => normalizeOpenAlexAuthorId(id))
            .filter(Boolean)
        : []
    );
    setAuthorVariants([]);
    setAuthorLoading(false);
    setAuthorLoadingError(null);
    setExtraPublicaciones([]);
    setPubLoading(false);
    setPubLoadingError(null);
    setPubSearchMode('author');
    setPendingIds(new Set());
    setShowSearchModal(false);
    setLibraryFilters({
      title: '',
      year_from: '',
      year_to: '',
      keyword: '',
    });
    setLibraryLoading(false);
    setLibraryError(null);
    setLibraryResults([]);
    setLibrarySelectedPubs([]);
    setLibraryActiveId(null);
    setLibraryActiveContainer(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigador?.id, initialAuthorSearchText]);

  useEffect(() => {
    if (subStep !== 1) return;

    if (authorSearchTimeoutRef.current) clearTimeout(authorSearchTimeoutRef.current);
    const q = authorSearchText.trim();
    if (!q || q.length < 2) {
      setAuthorVariants([]);
      setAuthorLoading(false);
      setAuthorLoadingError(null);
      return;
    }

    authorSearchTimeoutRef.current = setTimeout(() => {
      fetchAuthorCandidates(q);
    }, 800);

    return () => {
      if (authorSearchTimeoutRef.current) clearTimeout(authorSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStep, authorSearchText]);

  // Limpiar pending IDs cuando se complete el guardado exitosamente
  useEffect(() => {
    if (saveResult && !saveResult.errors?.length && saveResult.saved > 0) {
      setPendingIds(new Set());
    }
  }, [saveResult]);

  const getInitials = (nombre) => {
    if (!nombre) return '?';
    const parts = nombre.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return nombre[0].toUpperCase();
  };

  // Item activo para el overlay
  const activeItem = activeId ? 
    (databasePubs.find(p => p.mergedPublicationId === activeId) || 
     foundPubs.find(p => p.mergedPublicationId === activeId)) 
    : null;
  
  // Calcular publicaciones pendientes de guardar
  const pendingCount = pendingIds.size;
  
  // Verificar si hay publicaciones para guardar
  const hasPubsToSave = pendingCount > 0;

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-[#2D5BA6] dark:hover:text-[#5B8BD9] transition-colors mb-4"
        >
          <span className="material-icons-round text-base">arrow_back</span>
          Volver a publicaciones
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2D5BA6] to-blue-400 flex items-center justify-center text-white font-bold text-lg shadow-md">
              {getInitials(investigador.nombre)}
            </div>
            <div>
              <p className="font-bold text-slate-900 dark:text-white">{investigador.nombre}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{universidad.name}</p>
            </div>
          </div>

          {subStep === 2 && !pubLoading && !pubLoadingError && (
            <div className="sm:ml-auto flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="material-icons-round text-[#2D5BA6] text-base">article</span>
              <strong>{extraPublicaciones.length}</strong> publicaciones encontradas
            </div>
          )}
        </div>

        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Publicaciones extra</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Busca publicaciones adicionales usando variantes de nombre, el ID de OpenAlex del autor o el título de la publicación.
          Las obras de tipo preprint (borradores, p. ej. arXiv) no se listan ni se guardan.
          {subStep === 2 && (
            <>
              <br />
              <span className="text-[#2D5BA6] font-medium">
                💡 Tip: Arrastra publicaciones de "Encontradas" a "Base de Datos" para guardarlas
              </span>
            </>
          )}
        </p>
      </div>

      {/* Save Result Banner */}
      {saveResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-4 rounded-xl border flex items-start gap-3 ${
            saveResult.errors?.length > 0
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}
        >
          <span
            className={`material-icons-round text-2xl ${
              saveResult.errors?.length > 0 ? 'text-yellow-500' : 'text-green-500'
            }`}
          >
            {saveResult.errors?.length > 0 ? 'warning' : 'check_circle'}
          </span>
          <div>
            <p className="font-semibold text-slate-900 dark:text-white">
              {saveResult.saved} guardadas · {saveResult.duplicates} duplicadas
              {saveResult.errors?.length > 0 && ` · ${saveResult.errors.length} errores`}
            </p>
            {saveResult.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                {saveResult.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>• {e.title}: {e.error}</li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      )}

      {subStep === 1 && (
        <>
          {/* Author search */}
          <div className="flex flex-wrap gap-3 mb-2 items-end">
            <div className="relative flex-1 min-w-48">
              <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">
                search
              </span>
              <input
                type="text"
                value={authorSearchText}
                onChange={(e) => setAuthorSearchText(e.target.value)}
                placeholder="Buscar por nombre (ej: Maribel Maya) o ID de OpenAlex (ej: A5085173850)..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Selecciona variantes para agregar sus IDs, o continúa con los IDs existentes.
            </p>
            <button
              type="button"
              disabled={pubLoading}
              onClick={handleSkipAuthorSelection}
              className="text-sm text-[#2D5BA6] hover:text-[#244a8a] font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <span className="material-icons-round text-base">skip_next</span>
              Continuar con IDs existentes
            </button>
          </div>

          {authorLoading && (
            <div className="flex items-center justify-center py-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-slate-600 dark:text-slate-300 font-semibold">
                  Buscando variantes de autor en OpenAlex...
                </p>
              </div>
            </div>
          )}

          {!authorLoading && authorLoadingError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
              <span className="material-icons-round text-4xl text-red-400 mb-3 block">error_outline</span>
              <p className="text-red-700 dark:text-red-300 font-semibold">{authorLoadingError}</p>
            </div>
          )}

          {!authorLoading && !authorLoadingError && authorSearchText.trim().length >= 2 && authorVariants.length === 0 && (
            <div className="text-center py-12">
              <span className="material-icons-round text-6xl text-slate-300 dark:text-slate-600">search_off</span>
              <p className="text-slate-700 dark:text-slate-300 font-semibold mt-4">
                No se encontraron variantes para "{authorSearchText.trim()}"
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Prueba con otra variante del nombre del autor.
              </p>
            </div>
          )}

          {authorVariants.length > 0 && (
            <>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Selecciona las variantes que quieres agregar a tu perfil:
              </div>
              <div className="space-y-2 mb-4">
                {authorVariants.map((variant) => (
                  <AuthorVariantItem
                    key={variant.openAlexId}
                    variant={variant}
                    isSelected={selectedAuthorIds.has(variant.openAlexId)}
                    onToggle={toggleAuthorId}
                  />
                ))}
              </div>
            </>
          )}

          {authorVariants.length > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                disabled={selectedAuthorIds.size === 0 || saving}
                onClick={async () => {
                  try {
                    setSubStep(2);
                    await saveSelectedAndFetchPublications();
                  } catch (e) {
                    setPubLoading(false);
                    setPubLoadingError(e?.response?.data?.detail || 'Error guardando open_alex_ids');
                  }
                }}
                className="px-4 py-2 text-sm font-semibold bg-[#2D5BA6] hover:bg-[#244a8a] text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Guardar ID(s) seleccionados y buscar publicaciones
              </button>
            </div>
          )}
        </>
      )}

      {subStep === 2 && (
        <>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4 mb-4">
            <div className="flex items-start gap-3 mb-3">
              <span className="material-icons-round text-[#2D5BA6]">article_search</span>
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  Buscar por título de publicación
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Sirve cuando OpenAlex no trae bien la afiliación, pero sí encuentras la obra por su título exacto o por su ID/URL de OpenAlex.
                </p>
              </div>
            </div>
            <div className="mb-3 flex flex-col gap-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowOpenAlexHelp(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <span className="material-icons-round text-base">help</span>
                  Ver ayuda para buscar en OpenAlex
                </button>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-900/70 dark:bg-amber-950/30">
                <div className="flex items-start gap-2">
                  <span className="material-icons-round text-amber-600 dark:text-amber-400">warning_amber</span>
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                      Verificacion manual requerida
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      Usa solamente el titulo exacto o la URL completa de OpenAlex. Este flujo ahora muestra solo obras afiliadas a la IES del investigador o sin afiliacion institucional visible en OpenAlex. Antes de guardar, el investigador debe confirmar que la obra realmente corresponde al autor y a su institucion. La responsabilidad de esa validacion queda en quien realiza la busqueda.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={titleSearchText}
                onChange={(e) => {
                  setTitleSearchText(e.target.value);
                  if (pubLoadingError) setPubLoadingError(null);
                }}
                placeholder="Pega aquí el título exacto ..."
                className="flex-1 px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
              />
              <button
                type="button"
                onClick={searchPublicationsByTitle}
                disabled={pubLoading || titleSearchText.trim().length < 3}
                className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Buscar por título
              </button>
            </div>
          </div>



          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 py-3 px-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                <span className="text-slate-600 dark:text-slate-300">En base: {databasePubs.length - pendingCount}</span>
              </span>
              {pendingCount > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                  <span className="text-slate-600 dark:text-slate-300">Pendientes: {pendingCount}</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                <span className="text-slate-600 dark:text-slate-300">Por agregar: {foundPubs.length}</span>
              </span>
            </div>
            <button
              onClick={handleSave}
              disabled={!hasPubsToSave || saving}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold bg-[#2D5BA6] hover:bg-[#244a8a] text-white rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <span className="material-icons-round text-base">save</span>
                  Guardar {pendingCount > 0 ? `(${pendingCount})` : ''}
                </>
              )}
            </button>
          </div>

          {/* Results */}
          {pubLoading && (
            <div className="flex items-center justify-center py-10">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-[#2D5BA6] border-t-transparent rounded-full animate-spin" />
                <div className="text-center">
                  <p className="text-slate-700 dark:text-slate-300 font-semibold">Consultando OpenAlex...</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Esto puede tomar unos segundos dependiendo del número de resultados
                  </p>
                </div>
              </div>
            </div>
          )}

          {!pubLoading && pubLoadingError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
              <span className="material-icons-round text-4xl text-red-400 mb-3 block">error_outline</span>
              <p className="text-red-700 dark:text-red-300 font-semibold">{pubLoadingError}</p>
            </div>
          )}

          {!pubLoading && !pubLoadingError && extraPublicaciones.length === 0 && (
            <div className="text-center py-12">
              <span className="material-icons-round text-6xl text-slate-300 dark:text-slate-600">search_off</span>
              <p className="text-slate-700 dark:text-slate-300 font-semibold mt-4">
                {pubSearchMode === 'title'
                  ? 'No se encontraron publicaciones con ese título exacto en el rango 2020-2026'
                  : 'No se encontraron publicaciones afiliadas en el rango 2020-2026'}
              </p>
            </div>
          )}

          {!pubLoading && !pubLoadingError && extraPublicaciones.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Base de Datos - Izquierda */}
                <SortableContext
                  items={databasePubs.map(p => p.mergedPublicationId)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableContainer
                    id="database"
                    title="Base de Datos"
                    count={databasePubs.length}
                    icon="storage"
                    color={pendingCount > 0 ? 'yellow' : 'green'}
                  >
                    {databasePubs.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <span className="material-icons-round text-4xl block mb-2">inbox</span>
                        <p className="text-sm">No hay publicaciones guardadas</p>
                      </div>
                    ) : (
                      databasePubs.map((pub, idx) => (
                        <SortablePublicacionCard
                          key={pub.mergedPublicationId}
                          pub={pub}
                          index={idx}
                          containerId="database"
                          isPending={pendingIds.has(pub.mergedPublicationId)}
                        />
                      ))
                    )}
                  </DroppableContainer>
                </SortableContext>

                {/* Encontradas - Derecha */}
                <SortableContext
                  items={foundPubs.map(p => p.mergedPublicationId)}
                  strategy={verticalListSortingStrategy}
                >
                  <DroppableContainer
                    id="found"
                    title="Publicaciones Encontradas"
                    count={foundPubs.length}
                    icon="find_in_page"
                    color="amber"
                  >
                    {foundPubs.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <span className="material-icons-round text-4xl block mb-2">search_off</span>
                        <p className="text-sm">No hay nuevas publicaciones para agregar</p>
                        <p className="text-xs mt-1">Todas las encontradas ya están en la base de datos</p>
                      </div>
                    ) : (
                      foundPubs.map((pub, idx) => (
                        <SortablePublicacionCard
                          key={pub.mergedPublicationId}
                          pub={pub}
                          index={idx}
                          containerId="found"
                          isPending={false}
                        />
                      ))
                    )}
                  </DroppableContainer>
                </SortableContext>
              </div>

              {/* Overlay para el item siendo arrastrado */}
              <DragOverlay>
                {activeItem ? (
                  <DragOverlayCard pub={activeItem} containerId={activeContainer} />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </>
      )}

      {false && showSearchModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-6">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowSearchModal(false)}
            />
            <div className="relative w-full max-w-6xl rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    Buscar en el módulo de publicaciones
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Busca publicaciones ya existentes en la base y arrástralas para vincularlas con este investigador.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSearchModal(false)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <span className="material-icons-round">close</span>
                </button>
              </div>

              <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={libraryFilters.title}
                    onChange={(e) => setLibraryFilters((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Título contiene"
                    className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
                  />
                  <input
                    type="number"
                    value={libraryFilters.year_from}
                    onChange={(e) => setLibraryFilters((prev) => ({ ...prev, year_from: e.target.value }))}
                    placeholder="Año desde"
                    className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
                  />
                  <input
                    type="number"
                    value={libraryFilters.year_to}
                    onChange={(e) => setLibraryFilters((prev) => ({ ...prev, year_to: e.target.value }))}
                    placeholder="Año hasta"
                    className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
                  />
                  <input
                    type="text"
                    value={libraryFilters.keyword}
                    onChange={(e) => setLibraryFilters((prev) => ({ ...prev, keyword: e.target.value }))}
                    placeholder="Keyword"
                    className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5BA6] text-slate-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Resultados fuera del investigador actual: arrastra hacia la derecha para vincular.
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setLibraryFilters({ title: '', year_from: '', year_to: '', keyword: '' });
                        setLibraryResults([]);
                        setLibrarySelectedPubs([]);
                        setLibraryError(null);
                      }}
                      className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg"
                    >
                      Limpiar
                    </button>
                    <button
                      type="button"
                      onClick={fetchLibraryPublications}
                      disabled={libraryLoading}
                      className="px-4 py-2 text-sm font-semibold bg-[#2D5BA6] hover:bg-[#244a8a] text-white rounded-lg disabled:opacity-50"
                    >
                      {libraryLoading ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>
                </div>
                {libraryError && (
                  <p className="mt-3 text-sm text-red-600 dark:text-red-400">{libraryError}</p>
                )}
              </div>

              <div className="p-6">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleLibraryDragStart}
                  onDragEnd={handleLibraryDragEnd}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <SortableContext
                      items={libraryResults.map((p) => p.mergedPublicationId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <DroppableContainer
                        id="library-results"
                        title="Resultados del módulo"
                        count={libraryResults.length}
                        icon="manage_search"
                        color="blue"
                      >
                        {libraryResults.length === 0 ? (
                          <div className="text-center py-8 text-slate-400">
                            <span className="material-icons-round text-4xl block mb-2">search_off</span>
                            <p className="text-sm">No hay resultados cargados</p>
                          </div>
                        ) : (
                          libraryResults.map((pub, idx) => (
                            <SortablePublicacionCard
                              key={pub.mergedPublicationId}
                              pub={pub}
                              index={idx}
                              containerId="library-results"
                              isPending={false}
                            />
                          ))
                        )}
                      </DroppableContainer>
                    </SortableContext>

                    <SortableContext
                      items={librarySelectedPubs.map((p) => p.mergedPublicationId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <DroppableContainer
                        id="library-selected"
                        title="Seleccionadas para vincular"
                        count={librarySelectedPubs.length}
                        icon="publish"
                        color="yellow"
                      >
                        {librarySelectedPubs.length === 0 ? (
                          <div className="text-center py-8 text-slate-400">
                            <span className="material-icons-round text-4xl block mb-2">back_hand</span>
                            <p className="text-sm">Arrastra aquí las publicaciones a vincular</p>
                          </div>
                        ) : (
                          librarySelectedPubs.map((pub, idx) => (
                            <SortablePublicacionCard
                              key={pub.mergedPublicationId}
                              pub={pub}
                              index={idx}
                              containerId="library-selected"
                              isPending
                            />
                          ))
                        )}
                      </DroppableContainer>
                    </SortableContext>
                  </div>

                  <DragOverlay>
                    {libraryActiveId ? (
                      <DragOverlayCard
                        pub={
                          librarySelectedPubs.find((p) => p.mergedPublicationId === libraryActiveId) ||
                          libraryResults.find((p) => p.mergedPublicationId === libraryActiveId)
                        }
                        containerId={libraryActiveContainer}
                      />
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800/40">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {librarySelectedPubs.length} publicaciones listas para pasar al flujo principal.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowSearchModal(false)}
                    className="px-4 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg"
                  >
                    Cerrar
                  </button>
                  <button
                    type="button"
                    onClick={applyLibrarySelection}
                    className="px-4 py-2 text-sm font-semibold bg-[#2D5BA6] hover:bg-[#244a8a] text-white rounded-lg"
                  >
                    Agregar al flujo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showOpenAlexHelp && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Como buscar y copiar la URL en OpenAlex
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Este tutorial muestra como buscar la publicacion correcta con XPAC activo y copiar la URL para pegarla aqui.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowOpenAlexHelp(false)}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Cerrar ayuda"
              >
                <span className="material-icons-round">close</span>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-black">
                <video className="w-full h-auto max-h-[420px]" controls preload="metadata">
                  <source src="/tutorials/TutorialPublicaciones.mp4" type="video/mp4" />
                  <source src="/tutorials/TutorialPublicaciones.webm" type="video/webm" />
                  Tu navegador no soporta la reproduccion del video.
                </video>
              </div>
              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4">
                <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Pasos recomendados
                </p>
                <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <p>1. Busca el titulo exacto en OpenAlex.</p>
                  <p>2. Verificamos que pertenezca a la institucion y al autor correcto.</p>
                  <p>3. Copiamos el nombre de la publicacion en el sistema SIGC.</p>
                  <p>4. Revisamos en resultado y guardamos.</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <button
                type="button"
                onClick={() => setShowOpenAlexHelp(false)}
                className="px-4 py-2 text-sm font-semibold bg-[#2D5BA6] hover:bg-[#244a8a] text-white rounded-lg transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StepPublicacionesExtra;
